import React, { useState } from 'react';
import { AppLayout } from '../components/AppLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Search, LayoutDashboard, PlusCircle, Trophy, Megaphone, HardHat,
  ShieldCheck, FileSignature, Send, Shield, History, BarChart3,
  ChevronDown, ChevronRight, Users, Settings, Mail, Building2,
  Key, Calculator, Eye, FileText, Upload, Clock, UserCog, Tag,
  Ruler, Zap, Droplets, Truck, Camera, MessageSquare, Bell,
  Lock, RefreshCw, Download, Pencil, Check, X, ArrowUpDown
} from 'lucide-react';

const GUIDE_SECTIONS = [
  {
    id: 'dashboard',
    title: 'Tableau de Bord',
    icon: LayoutDashboard,
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
    description: 'Vue d\'ensemble de toutes les simulations CAPEX et KPIs de performance.',
    subsections: [
      {
        title: 'KPIs Principaux',
        content: `Le tableau de bord affiche les indicateurs clés en temps réel :
- **En cours d'étude** : Nombre de simulations en attente de décision
- **GO** : Simulations validées par la direction
- **NO GO / Perdus** : Simulations refusées ou perdues
- **Contre-propositions en cours** : Simulations avec une contre-proposition en attente de réponse
- **Dossiers Signés** : Nombre total de contrats signés
- **CAPEX engagé** : Montant total des investissements signés
- **CA Total (GO)** : Chiffre d'affaires prévisionnel des dossiers GO
- **CA Moyen** : CA moyen par dossier GO
- **Taux Commission Moyen** : Commission moyenne sur les dossiers GO
- **Payback Moyen** : Délai de retour sur investissement moyen`
      },
      {
        title: 'Info d\'Or (Widget)',
        content: `Section dédiée aux opportunités commerciales remontées :
- Nombre total d'Info d'Or
- Nombre "À traiter" (en attente)
- Nombre "Signés"
- CA Signé (total des primes associées)
- Alerte visuelle quand des opportunités sont en attente de traitement`
      },
      {
        title: 'Liste des simulations',
        content: `Tableau interactif de toutes les simulations accessibles :
- **Filtres** : Par statut (GO, NOGO, En cours), par porteur CAPEX (CCD/Client/Mixte), par commercial, par type de dossier
- **Tri** : Par date, par CA, par CAPEX
- **Recherche** : Par nom de projet, créateur, enseigne
- **Badges visuels** : "Envoyé" (email envoyé à la direction), "Contrat" (demande de contrat effectuée), "Brouillon" (brouillon de contrat sauvegardé)
- **Actions rapides** : Valider/Refuser directement depuis le tableau (admin)
- **Statut métier** : Dossier en cours / Dossier signé / NO GO / Perdu
- Clic sur une ligne → accès au détail complet de la simulation`
      },
      {
        title: 'Périodes et Filtres temporels',
        content: `Basculer entre différentes vues temporelles :
- **Mois** : Simulations du mois en cours
- **Trimestre** : Simulations du trimestre
- **Semestre** : Simulations du semestre
- **Année** : Vue annuelle complète
Bouton de rafraîchissement pour recharger les données.`
      },
      {
        title: 'Vue Analytics',
        content: `Accessible via le bouton "Analytics" en haut du dashboard :
- Graphiques de répartition par statut
- Évolution temporelle des simulations
- Répartition par enseigne
- Analyse de la performance commerciale`
      },
      {
        title: 'Vue Utilisateur Standard',
        content: `Le bouton "Vue Utilisateur" permet à un admin de voir le dashboard tel qu'un commercial le verrait, avec les restrictions de visibilité normales. Utile pour vérifier les droits d'accès.`
      }
    ]
  },
  {
    id: 'simulation',
    title: 'Nouvelle Simulation',
    icon: PlusCircle,
    color: 'text-indigo-600',
    bgColor: 'bg-indigo-50',
    borderColor: 'border-indigo-200',
    description: 'Création et édition de simulations d\'investissement CAPEX en 5 étapes.',
    subsections: [
      {
        title: 'Étape 1 — Informations Générales',
        content: `Données de base du projet :
- **Nom du projet** : Identifiant unique de la simulation
- **Type de projet** : Nouvelle ouverture, Remplacement, Rénovation
- **Enseigne** : Leclerc, Intermarché, Système U, Carrefour, Auchan
- **Adresse** : Saisie avec autocomplétion Google Maps (adresse, code postal, ville)
- **Surface magasin** (m²)
- **Zone de chalandise** : Zone RM, Âge moyen, CSP, Revenu médian
- **Date d'ouverture souhaitée**`
      },
      {
        title: 'Étape 2 — Données Restaurant',
        content: `Paramètres financiers du restaurant :
- **CA prévisionnel HT** (chiffre d'affaires estimé)
- **Nombre de couverts/jour**
- **Panier moyen**
- **Jours d'ouverture par semaine**
- **Horaires d'ouverture**`
      },
      {
        title: 'Étape 3 — Charges et Coûts',
        content: `Structure de coûts pour le P&L :
- **Food Cost** : Taux automatique basé sur le cluster CA (modifiable)
- **Other Cost** : Idem, taux automatique par cluster
- **Main d'œuvre** : Grille horaire configurable depuis l'admin
- **Commission** : Taux de commission applicable
Les clusters sont automatiquement sélectionnés selon le CA prévisionnel.`
      },
      {
        title: 'Étape 4 — Investissement CAPEX',
        content: `Détail de l'investissement :
- **Porteurs CAPEX** : Corner (CCD ou Client), Vitrine (CCD ou Client), Inox (CCD ou Client)
- **Concept** : WASABI, MME WANG, SORAE, POKAWA
- **Emplacement du corner** : Zone Entrée, Zone Fleg, Zone Allée Promo, etc.
- **Note de l'emplacement** : Incroyable, Très bon, Bon, Moyen, etc.
- **Projet du corner** : 4 faces on tourne, 3 faces contre un mur, Atypique
- **Montants** : CAPEX Corner, Vitrine, Inox, Informatique/Caisse, Frais locaux/juridiques, Robots
- **Équipements** : Cellule de refroidissement (fournisseur, sur corner), Wok (idem)`
      },
      {
        title: 'Étape 5 — Récapitulatif & Calcul',
        content: `Validation finale et lancement du calcul :
- Résumé de toutes les données saisies
- Vérification des champs obligatoires
- Bouton "Calculer" → génère 3 hypothèses (basse, moyenne, haute)
- Résultat : P&L mensuel, Payback, Mois rouges, Verdict de viabilité`
      }
    ]
  },
  {
    id: 'result',
    title: 'Résultat Simulation',
    icon: Calculator,
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-50',
    borderColor: 'border-emerald-200',
    description: 'Analyse complète du résultat de la simulation CAPEX.',
    subsections: [
      {
        title: 'Vue Résultat',
        content: `Après calcul, la page de résultat affiche :
- **3 Hypothèses** : Basse, Moyenne, Haute — chacune avec son P&L, payback, et statut (Viable/Risqué/Non viable)
- **Hypothèse recommandée** : Celle qui offre le meilleur compromis
- **Graphiques** : P&L mensuel, évolution du cashflow cumulé
- **Détail CAPEX** : Répartition de l'investissement par poste
- **Verdict** : Score global avec nombre de mois rouges`
      },
      {
        title: 'Actions Admin',
        content: `L'administrateur peut :
- **Valider (GO)** / **Refuser (NOGO)** la simulation
- **Envoyer à la Direction** : Génère un email avec PDF et Excel joints
- **Contre-proposition** : Proposer un CA et/ou commission différents
- **Changer le statut métier** : En cours → Signé / Perdu / NO GO
- **Prérequis Techniques** : Bouton jaune pour créer/voir les prérequis (bloque la signature si non validé)`
      },
      {
        title: 'Exports',
        content: `Depuis la page résultat :
- **PDF** : Rapport complet avec graphiques, P&L, détails CAPEX
- **Excel** : Données brutes exportables
- **Demande de Contrat** : Génère et envoie une demande de contrat aux destinataires configurés`
      }
    ]
  },
  {
    id: 'golden-info',
    title: 'Info d\'Or',
    icon: Trophy,
    color: 'text-amber-600',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-200',
    description: 'Module de remontée et suivi des opportunités commerciales.',
    subsections: [
      {
        title: 'Création d\'une Info d\'Or',
        content: `Toute personne peut remonter une opportunité :
- **Enseigne** : Intermarché, Leclerc, Système U, Carrefour, Auchan
- **Adresse, Code postal, Ville**
- **Contact** : Nom, Fonction, Téléphone
- **Commentaires** : Contexte de l'opportunité
- **Informateur** : Personne à l'origine de l'info (par défaut le créateur)
Un email de confirmation est envoyé au créateur + notification aux admin.`
      },
      {
        title: 'Pipeline de statuts',
        content: `Chaque Info d'Or suit un pipeline :
1. **À traiter** : Nouvelle opportunité en attente
2. **Pris en compte** : L'équipe commerciale a pris connaissance
3. **RDV pris** : Un rendez-vous est planifié avec le magasin
4. **Dossier signé** : Le dossier a abouti (+ CA prévisionnel, mois de contrat, taux de commission)
5. **Dossier non signé** : L'opportunité n'a pas abouti
Seuls les admins peuvent changer le statut.`
      },
      {
        title: 'Système de Prime (200€)',
        content: `Lorsqu'une Info d'Or aboutit en signature :
- Une **prime de 200€** est attribuée à l'informateur
- La colonne "Prime" affiche le montant dans le tableau
- Le KPI "CA Signé" sur le dashboard affiche le total des primes`
      },
      {
        title: 'Relances automatiques',
        content: `Système de relance automatique tous les **14 jours** :
- Envoie un email au dernier destinataire de l'Info d'Or
- Met en copie le créateur + tous les admin/super_admin
- Ne s'applique qu'aux Info d'Or non clôturées (ni signées, ni non signées)
- Configurable depuis l'admin (intervalle, activation, template)
- Relance manuelle possible via le bouton dédié`
      },
      {
        title: 'Recherche et filtres',
        content: `- Recherche par informateur, enseigne, ville, contact
- Filtres par statut, par enseigne
- Export Excel de toutes les Info d'Or
- Téléchargement PDF individuel
- Envoi par email à un destinataire`
      }
    ]
  },
  {
    id: 'announcements',
    title: 'Annonces ALL',
    icon: Megaphone,
    color: 'text-green-600',
    bgColor: 'bg-green-50',
    borderColor: 'border-green-200',
    description: 'Système d\'annonce pour diffuser les nouvelles signatures à toute l\'entreprise.',
    subsections: [
      {
        title: 'Déclenchement',
        content: `L'annonce ALL se déclenche depuis la page résultat d'une simulation signée :
- Bouton vert "ALL" visible uniquement sur les dossiers signés
- Ouvre un formulaire pré-rempli avec les données de la simulation`
      },
      {
        title: 'Contenu de l\'annonce',
        content: `Le formulaire Annonce ALL contient :
- **Data du Magasin** : Enseigne, Ville, Adresse, Surface
- **Informations Projet** : Emplacement, Note, Projet corner, Porteur CAPEX, Collaborateurs, Date d'ouverture estimée
- **Décision Direction** : Porteur CAPEX, CAPEX total, CA Prévisionnel HT, Commission, Contribution Margin
- **Investissement CAPEX** : Détail des porteurs (Corner, Vitrine, Inox)
- **Commande à passer** : Écran d'affichage, Meuble Wok, Cellule de refroidissement (Oui/Non)
- **Particularité** : Champ texte libre
- **Pièces jointes** : Upload de documents (contrat signé, visuels, etc.) avec catégories`
      },
      {
        title: 'Workflow d\'approbation',
        content: `1. Le commercial crée l'annonce → statut "En attente"
2. L'admin reçoit une notification (badge dans le menu)
3. L'admin peut : Approuver / Refuser / Modifier le texte
4. À l'approbation → email envoyé à tous les destinataires configurés
5. Email avec bandeau rouge "NOUVELLE SIGNATURE" et toutes les données`
      },
      {
        title: 'Gestion des destinataires',
        content: `- Multi-destinataires configurables en admin (ex: all@wasabicorner.fr)
- Possibilité d'ajouter des destinataires ponctuels avant envoi
- Sélection par checkbox des destinataires actifs`
      }
    ]
  },
  {
    id: 'chantier',
    title: 'Pilotage Chantier',
    icon: HardHat,
    color: 'text-orange-600',
    bgColor: 'bg-orange-50',
    borderColor: 'border-orange-200',
    description: 'Module de suivi de chantier avec checklist de validation et PV de livraison.',
    subsections: [
      {
        title: 'Liste des chantiers',
        content: `Vue d'ensemble de tous les chantiers :
- **KPIs** : Total, Nouveau passage requis, En attente, Validés
- Répartition par statut et par organisation (admin)
- Filtres par statut, organisation, enseigne
- Délai moyen création → soumission`
      },
      {
        title: 'Formulaire Chantier (Prestataire)',
        content: `Formulaire multi-étapes accessible aux prestataires :
- **Identification** : Marques, Enseigne, Nom du magasin, Adresse, Intervenant(s)
- **Checklist dynamique** : 20+ points de validation configurables en admin, regroupés par catégorie (Pose & Installation, Tests & Contrôles, Finitions)
  - Chaque point : Oui / Non / Partiellement + Commentaire conditionnel
  - Barre de progression par catégorie
- **Photos** : Upload de 20 photos max avec aperçu et suppression
- **Observations** : Champ texte libre
- **Repassage** : Oui/Non + Date de repassage si nécessaire
- **Signature** : Zone de signature tactile
- **Soumission** : Envoi du PV avec email automatique aux destinataires`
      },
      {
        title: 'Synthèse Chantier',
        content: `Page complète affichant toutes les données du chantier :
- Identification, Vérifications checklist, Photos, Observations, Historique
- **Diffuser le PV** : Bouton admin pour envoyer le PDF aux destinataires (avec modale de vérification)
- **Télécharger PDF** : Export PDF complet avec checklist, photos, signature
- **Modifier** : Redirige vers le formulaire d'édition`
      },
      {
        title: 'Validation Admin',
        content: `L'admin peut :
- **Valider** le chantier
- **Refuser** avec commentaire
- **Demander des corrections**
Email de notification envoyé au prestataire à chaque changement de statut.`
      }
    ]
  },
  {
    id: 'prerequis',
    title: 'Prérequis Techniques',
    icon: ShieldCheck,
    color: 'text-amber-600',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-200',
    description: 'Module de vérification technique des conditions d\'installation avant signature CAPEX.',
    subsections: [
      {
        title: 'Création et préremplissage',
        content: `Accessible depuis la page résultat CAPEX (bouton jaune) ou le menu latéral :
- Préremplissage automatique depuis la simulation : Magasin, Enseigne, Adresse, Code postal, Ville
- Préremplissage depuis l'étape 4 CAPEX : Zone d'implantation, Note de l'emplacement, Projet du corner`
      },
      {
        title: 'Bloc 1 — Identification du site',
        content: `- Magasin, Enseigne (prérempli)
- Adresse, Code postal, Ville (prérempli)
- Contact magasin : Nom, Fonction, Téléphone
- Date de visite terrain`
      },
      {
        title: 'Bloc 2 — Emplacement',
        content: `- Zone d'implantation (prérempli depuis CAPEX)
- Note de l'emplacement (lecture seule, depuis CAPEX)
- Projet du corner (lecture seule, depuis CAPEX)
- Emplacement validé par le magasin (Oui/Non) → Si Non : alerte bloquante`
      },
      {
        title: 'Bloc 3 — Dimensions au sol',
        content: `- Profondeur (cm), Largeur (cm) → Surface calculée automatiquement
- Obstacle au sol (Oui/Non + commentaire)
- Commentaires libres`
      },
      {
        title: 'Bloc 4 — Hauteur et plafond',
        content: `- Hauteur sous plafond (cm)
- Hauteur libre réelle (cm) → Alerte bloquante si < 270 cm
- Obstacles au plafond (multi-sélection)
- Référence : Hauteur standard enseigne aérienne Wasabi = 270 cm`
      },
      {
        title: 'Bloc 5 — Spécificités Intermarché',
        content: `Visible uniquement si Enseigne = Intermarché :
- Herse décorative (Oui/Non/Prévue/Inconnu) → Alerte bloquante si Inconnu
- Hauteur sous herse, Largeur herse, Position`
      },
      {
        title: 'Bloc 6 — Électricité',
        content: `- Présence alimentation électrique (Oui/Non) → Bloquant si Non
- Type : Monophasé/Triphasé/Inconnu
- Distance au tableau électrique
- Puissance connue (Oui/Non + valeur)
- Tableau électrique accessible
- Travaux nécessaires`
      },
      {
        title: 'Bloc 7 — Eau',
        content: `- Eau froide (Oui/Non)
- Eau chaude (Oui/Non)
- Évacuation (Oui/Non) → Alerte technique si Non
- Distance (m)
- Faisabilité raccordement`
      },
      {
        title: 'Bloc 8 — Logistique',
        content: `- Accès livraison : Conforme/Difficile/Impossible → Bloquant si Impossible
- Distance au quai
- Semi-remorque possible
- Chariot élévateur disponible (+ jour d'installation)
- Zone de stockage (localisation, capacité)
- Chemin d'accès dégagé
- Contraintes de manutention`
      },
      {
        title: 'Bloc 9 — Photos obligatoires',
        content: `7 catégories de photos obligatoires uploadées via Object Storage :
1. Vue globale
2. Dimensions au sol
3. Électricité
4. Eau
5. Plafond
6. Obstacles sol
7. Accès livraison
+ Photos complémentaires (herse, alimentation électrique, mètre visible, etc.)`
      },
      {
        title: 'Bloc 10 — Commentaires',
        content: `- Observations terrain (texte libre)
- Risques identifiés
- Demandes spécifiques
- Points à confirmer`
      },
      {
        title: 'Système d\'alertes et score',
        content: `3 niveaux d'alertes calculés automatiquement :
- **Conforme** (vert) : Aucun problème détecté
- **Vigilance** (orange) : Points d'attention à surveiller
- **Bloquant** (rouge) : Éléments empêchant la finalisation
Le score global détermine si le prérequis peut être soumis.`
      },
      {
        title: 'Workflow de validation',
        content: `Pipeline de statuts :
1. **Brouillon** → En cours de rédaction
2. **Soumis** → Envoyé pour validation technique (email aux destinataires)
3. **Validé** / **Validé avec réserves** / **Refusé** / **À corriger** (email au créateur + destinataires)
Blocage CAPEX : La signature d'une simulation est impossible tant que le prérequis n'est pas validé.`
      },
      {
        title: 'Dashboard Prérequis',
        content: `Page liste avec :
- Stats : Total, Brouillons, Incomplets, En validation, Validés, Refusés
- Filtres : Recherche, Statut, Enseigne, Score, Créateur, Date
- Vue tableau desktop + Vue cards mobile`
      }
    ]
  },
  {
    id: 'contract',
    title: 'Demande de Contrat',
    icon: FileSignature,
    color: 'text-teal-600',
    bgColor: 'bg-teal-50',
    borderColor: 'border-teal-200',
    description: 'Gestion des demandes de contrat pour les simulations validées.',
    subsections: [
      {
        title: 'Page dédiée',
        content: `Accessible via le menu latéral "Demande de Contrat" :
- **KPIs** : Total demandes, Brouillons, Envoyées, Reçues, Signées, Refusées
- **Pipeline visuel** : Répartition par statut
- **Tableau filtrable** : Recherche, filtre par statut, tri par date
- **Détail modal** : Clic sur une ligne pour voir le détail complet
- **Export Excel** : Export de toutes les demandes
- **Changement de statut** : Admin peut faire avancer le pipeline`
      },
      {
        title: 'Brouillon de contrat',
        content: `Depuis la page résultat d'une simulation :
- **Sauvegarder un brouillon** : Les données du contrat sont sauvegardées sans envoyer
- Badge "Brouillon" visible sur le dashboard principal
- Possibilité de reprendre et modifier avant envoi`
      }
    ]
  },
  {
    id: 'agenceur',
    title: 'Commande Corner',
    icon: Send,
    color: 'text-purple-600',
    bgColor: 'bg-purple-50',
    borderColor: 'border-purple-200',
    description: 'Module de gestion des commandes corner et dossiers agenceur.',
    subsections: [
      {
        title: 'Création d\'un dossier',
        content: `L'admin crée un dossier agenceur avec :
- Simulation CAPEX associée
- Prestataire agenceur (auto-rempli depuis le back-office)
- Date prévisionnelle d'installation (+8 semaines par défaut)
- Montant du devis
- Plan d'installation (upload obligatoire)
- Démontage ancien corner (Oui/Non avec sous-questions conditionnelles)`
      },
      {
        title: 'Envoi au prestataire',
        content: `- Email professionnel avec bandeau orange et bouton CTA
- Le prestataire reçoit un lien unique (sans connexion requise)
- Page publique de réponse : Accepter / Refuser / Demander des modifications
- Upload de documents par le prestataire`
      },
      {
        title: 'SLA et relances',
        content: `- Système de SLA configurable (délai par défaut : 72h)
- Relances automatiques à J+72h puis J+120h
- Alerte vigilance si date prévisionnelle < 8 semaines
- KPIs : Total dossiers, En attente, Acceptés, Refusés, Délai moyen`
      }
    ]
  },
  {
    id: 'admin',
    title: 'Administration',
    icon: Shield,
    color: 'text-red-600',
    bgColor: 'bg-red-50',
    borderColor: 'border-red-200',
    description: 'Back-office complet pour la gestion de la plateforme.',
    subsections: [
      {
        title: 'Utilisateurs',
        content: `Gestion complète des utilisateurs :
- Liste avec rôle, email, date d'inscription, dernière connexion, statut
- **Rôles** : Super Admin, Admin, DG, DOP, Directeur Commercial, Directeur Régional, Business Manager, Commercial, Assistante Commerciale, Prestataire, Readonly
- **Actions** : Créer, Modifier le rôle, Supprimer, Approuver/Refuser
- **Visibilité** : Configurer quelles simulations chaque utilisateur peut voir (propres / spécifiques / toutes)
- **Historique connexions** : Voir l'historique de connexion par utilisateur (date, heure, IP)`
      },
      {
        title: 'Clusters',
        content: `Configuration des clusters de coûts :
- Chaque cluster correspond à une tranche de CA
- Taux Food Cost, Other Cost, Main d'œuvre, Commission, Contribution Margin
- Import/Export Excel
- Historique des modifications
- Bouton "Recalculer toutes les simulations" pour appliquer les nouveaux taux`
      },
      {
        title: 'Destinataires',
        content: `Gestion des listes de destinataires par module :
- **Destinataires généraux** : Reçoivent les emails de simulation
- **Destinataires HG** : Reçoivent les exports HG/Direction
- **Destinataires Contrat** : Reçoivent les demandes de contrat
- **Destinataires Prérequis** : Reçoivent les notifications de soumission/validation des prérequis techniques
Pour chaque : Ajouter (email + nom), Supprimer`
      },
      {
        title: 'Organisations',
        content: `Gestion des organisations prestataires :
- Créer/Modifier/Supprimer des organisations
- Affecter des utilisateurs à une organisation
- Isolation des données par organisation (un prestataire ne voit que les chantiers de son org)`
      },
      {
        title: 'Prestataires Agenceurs',
        content: `Back-office pour gérer les prestataires agenceurs :
- CRUD : Nom, Email
- Auto-remplissage de l'email dans le formulaire Dossier Agenceur`
      },
      {
        title: 'Checklist Chantier',
        content: `Configuration dynamique des points de checklist :
- Ajouter/Modifier/Supprimer des points
- Catégories : Pose & Installation, Tests & Contrôles, Finitions
- Options par point : Oui/Non/Partiellement
- Commentaire conditionnel (sur quelles réponses)
- Réorganisation par glisser-déposer
- Activation/Désactivation individuelle`
      },
      {
        title: 'Catégories Upload',
        content: `Gestion des catégories pour les pièces jointes Annonce ALL :
- Créer/Modifier/Supprimer des catégories (ex: Contrat, Visuel validé)
- Activation/Désactivation`
      },
      {
        title: 'Routage Emails',
        content: `Configuration fine de qui reçoit quel type d'email :
- Matrice croisée : Types d'emails × Utilisateurs admin
- Toggle ON/OFF par combinaison
- Historique des modifications (audit log)`
      },
      {
        title: 'Accès Prérequis',
        content: `Contrôle d'accès au module Prérequis Techniques :
- Liste de tous les utilisateurs avec switch bloquer/débloquer
- Recherche par nom/email/rôle
- Les utilisateurs bloqués ne peuvent ni créer ni consulter les fiches prérequis`
      },
      {
        title: 'Permissions',
        content: `Système de permissions par rôle (Super Admin uniquement) :
- Matrice croisée : Permissions × Rôles
- Permissions disponibles : Consulter, Exporter, Valider, Diffuser, Envoyer HG, etc.
- Reset aux valeurs par défaut possible`
      },
      {
        title: 'Templates Email',
        content: `Personnalisation des templates d'emails :
- Modifier le sujet et le corps de chaque type d'email
- Variables dynamiques disponibles (nom, enseigne, ville, etc.)
- Reset au template par défaut`
      },
      {
        title: 'SLA Agenceur',
        content: `Paramétrage des délais SLA pour les dossiers agenceur :
- Délai SLA (heures)
- Relance 1 (heures)
- Relance 2 (heures)`
      },
      {
        title: 'Diagnostic',
        content: `Outils de diagnostic système :
- Test de la configuration email (Resend)
- Vérification de la clé API
- Test d'envoi d'email
- Informations système (URLs Frontend/Backend)`
      }
    ]
  },
  {
    id: 'profile',
    title: 'Profil & Autres',
    icon: Users,
    color: 'text-slate-600',
    bgColor: 'bg-slate-50',
    borderColor: 'border-slate-200',
    description: 'Gestion du profil, historique, et fonctionnalités transversales.',
    subsections: [
      {
        title: 'Profil utilisateur',
        content: `- Modifier le nom, email, mot de passe
- Voir son rôle et ses permissions
- Thème sombre/clair`
      },
      {
        title: 'Historique des simulations',
        content: `Page dédiée listant toutes les simulations :
- Recherche et filtres avancés
- Comparaison côte à côte de plusieurs simulations`
      },
      {
        title: 'Mot de passe oublié',
        content: `Flux complet de réinitialisation :
1. Saisir son email sur la page de connexion
2. Recevoir un lien de réinitialisation par email
3. Créer un nouveau mot de passe
L'admin peut aussi déclencher une réinitialisation depuis le back-office.`
      },
      {
        title: 'Multilingue',
        content: `L'application supporte le français et l'anglais :
- Bouton de switch dans le menu latéral
- Tous les labels et messages sont traduits`
      }
    ]
  }
];

const SectionBlock = ({ section, searchQuery }) => {
  const [open, setOpen] = useState(false);
  const Icon = section.icon;

  const filteredSubs = searchQuery
    ? section.subsections.filter(s =>
        s.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        s.content.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : section.subsections;

  if (searchQuery && filteredSubs.length === 0) return null;

  const isOpen = open || !!searchQuery;

  return (
    <Card className={`border ${section.borderColor} overflow-hidden`} data-testid={`guide-section-${section.id}`}>
      <button
        onClick={() => setOpen(!open)}
        className={`w-full flex items-center justify-between p-5 ${section.bgColor} hover:brightness-95 transition-all text-left`}
      >
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg bg-white/80 ${section.color}`}>
            <Icon className="w-6 h-6" />
          </div>
          <div>
            <h2 className={`text-lg font-bold ${section.color}`}>{section.title}</h2>
            <p className="text-sm text-slate-500 mt-0.5">{section.description}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="secondary" className="text-xs">{section.subsections.length} sections</Badge>
          {isOpen ? <ChevronDown className="w-5 h-5 text-slate-400" /> : <ChevronRight className="w-5 h-5 text-slate-400" />}
        </div>
      </button>
      {isOpen && (
        <CardContent className="p-0 divide-y divide-slate-100">
          {filteredSubs.map((sub, idx) => (
            <SubSection key={idx} sub={sub} searchQuery={searchQuery} />
          ))}
        </CardContent>
      )}
    </Card>
  );
};

const SubSection = ({ sub, searchQuery }) => {
  const [open, setOpen] = useState(!!searchQuery);

  return (
    <div className="border-l-4 border-transparent hover:border-blue-400 transition-colors">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-6 py-3 hover:bg-slate-50 transition-colors text-left"
      >
        <span className="font-semibold text-slate-700 text-sm">{sub.title}</span>
        {open ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
      </button>
      {open && (
        <div className="px-6 pb-4 text-sm text-slate-600 leading-relaxed whitespace-pre-line">
          {sub.content.split('\n').map((line, i) => {
            const trimmed = line.trim();
            if (trimmed.startsWith('- **')) {
              const match = trimmed.match(/^- \*\*(.+?)\*\*\s*:?\s*(.*)/);
              if (match) {
                return (
                  <div key={i} className="flex gap-2 ml-2 my-1">
                    <span className="text-slate-400 mt-0.5">-</span>
                    <span><strong className="text-slate-800">{match[1]}</strong>{match[2] ? ` : ${match[2]}` : ''}</span>
                  </div>
                );
              }
            }
            if (trimmed.startsWith('- ')) {
              return <div key={i} className="flex gap-2 ml-2 my-0.5"><span className="text-slate-400">-</span><span>{trimmed.slice(2)}</span></div>;
            }
            if (/^\d+\./.test(trimmed)) {
              return <div key={i} className="flex gap-2 ml-2 my-1"><span className="text-blue-500 font-bold">{trimmed.match(/^\d+/)[0]}.</span><span>{trimmed.replace(/^\d+\.\s*/, '')}</span></div>;
            }
            if (!trimmed) return <div key={i} className="h-2" />;
            return <p key={i} className="my-1">{trimmed}</p>;
          })}
        </div>
      )}
    </div>
  );
};

export default function GuidePage() {
  const [search, setSearch] = useState('');

  const matchCount = search
    ? GUIDE_SECTIONS.reduce((acc, s) => acc + s.subsections.filter(sub =>
        sub.title.toLowerCase().includes(search.toLowerCase()) ||
        sub.content.toLowerCase().includes(search.toLowerCase())
      ).length, 0)
    : 0;

  return (
    <AppLayout>
      <div className="space-y-6 max-w-5xl mx-auto" data-testid="guide-page">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Guide de l'application</h1>
          <p className="text-slate-500 mt-1">Documentation complète de toutes les fonctionnalités CAPEX By Wasabi Business</p>
        </div>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <Input
            placeholder="Rechercher une fonctionnalité..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-11 h-12 text-base"
            data-testid="guide-search"
          />
          {search && (
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">
              {matchCount} résultat{matchCount !== 1 ? 's' : ''}
            </span>
          )}
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {GUIDE_SECTIONS.map(s => {
            const Icon = s.icon;
            return (
              <button
                key={s.id}
                onClick={() => document.querySelector(`[data-testid="guide-section-${s.id}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                className={`flex items-center gap-2 p-3 rounded-lg border ${s.borderColor} ${s.bgColor} hover:brightness-95 transition-all text-left`}
              >
                <Icon className={`w-4 h-4 ${s.color}`} />
                <span className={`text-xs font-semibold ${s.color}`}>{s.title}</span>
              </button>
            );
          })}
        </div>

        <div className="space-y-4">
          {GUIDE_SECTIONS.map(s => (
            <SectionBlock key={s.id} section={s} searchQuery={search} />
          ))}
        </div>

        <p className="text-center text-xs text-slate-400 pb-8">CAPEX By Wasabi Business — Guide v1.0 — {new Date().toLocaleDateString('fr-FR')}</p>
      </div>
    </AppLayout>
  );
}
